import React, { useState } from 'react';
import { api, CdnAuditReport } from '../api';
import { Callout, Input, Section } from './primitives';
import { OperationFailure, OperationResult, useOperationRun } from './operation/OperationResult';
import { actionLabel } from './operation/actionLabels';
import { SshStat, SshStats } from './SshStats';
import { StatusBadge } from './ui';

interface CdnAuditCardProps {
  environmentId: string;
}

type Verdict = { tone: 'success' | 'warning' | 'danger' | 'neutral'; verdict: string };

const verdictOf: Record<CdnAuditReport['overall_status'], Verdict> = {
  optimal: { tone: 'success', verdict: 'The storefront is ready for a CDN' },
  needs_tuning: { tone: 'warning', verdict: 'The storefront needs tuning before a CDN' },
  unoptimized: { tone: 'danger', verdict: 'The storefront is not ready for a CDN' },
};

function answerOf(report: CdnAuditReport): Verdict {
  // A score over zero routes says nothing: do not present it as a verdict.
  if (report.tested_urls_count === 0) return { tone: 'neutral', verdict: 'No routes were checked' };
  return verdictOf[report.overall_status] ?? { tone: 'neutral', verdict: `The check finished with a verdict this page does not know (${report.overall_status})` };
}

function coverage(report: CdnAuditReport, url: string): string {
  const routes = `${report.tested_urls_count} ${report.tested_urls_count === 1 ? 'route' : 'routes'} requested`;
  return `${routes}${url ? ` from ${url}` : ''}: response headers, cookies set on public pages and X-Magento-Tags.`;
}

export const CdnAuditCard: React.FC<CdnAuditCardProps> = ({ environmentId }) => {
  const [storefrontUrl, setStorefrontUrl] = useState('');
  const { running, result, failure, run } = useOperationRun(async () => {
    const url = storefrontUrl.trim();
    return { report: await api.runCdnAudit(environmentId, storefrontUrl), url };
  }, 'The server gave no reason.');
  const report = result?.value.report ?? null;
  const incomplete = report && !report.succeeded ? report : null;
  const answer = report && report.succeeded ? answerOf(report) : null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    void run();
  };

  return (
    <Section
      title="CDN and cache readiness"
      testId="cdn-audit"
      actions={
        <form className="ssh-run-form" onSubmit={submit}>
          <Input
            type="text"
            placeholder="https://store.example.com (optional)"
            value={storefrontUrl}
            onChange={(e) => setStorefrontUrl(e.target.value)}
            aria-label="Storefront URL"
          />
          <button type="submit" disabled={running} className="button button--secondary">
            {running ? 'Checking headers' : failure ? actionLabel.retry : report ? 'Check again' : 'Check CDN readiness'}
          </button>
        </form>
      }
    >
      {failure ? (
        <OperationFailure
          verdict="The CDN check did not finish"
          at={failure.at}
          scope="Nothing was changed"
          reason={failure.message}
          testId="cdn-audit-failure"
        />
      ) : null}

      {incomplete && result ? (
        <OperationFailure
          verdict="The CDN check did not finish"
          at={result.at}
          scope="Incomplete: score and routes are not shown"
          output={incomplete.error_output}
          testId="cdn-audit-incomplete"
        />
      ) : null}

      {report && answer && result ? (
        <OperationResult tone={answer.tone} verdict={answer.verdict} at={result.at} scope={coverage(report, result.value.url)} testId="cdn-audit-result">
          <SshStats>
            {/* The verdict above already says ready or not: the score is the extra fact. */}
            <SshStat label="Readiness score" value={`${report.readiness_score}%`} />
            <SshStat label="Cacheable pages" value={`${report.cacheable_count} of ${report.tested_urls_count}`} detail="URLs checked" />
            <SshStat
              label="Cookies on public pages"
              value={
                <StatusBadge
                  kind="auditResult"
                  value={report.leak_cookie_count === 0 ? 'passed' : 'warning'}
                  label={report.leak_cookie_count === 0 ? 'None' : `${report.leak_cookie_count} ${report.leak_cookie_count === 1 ? 'route' : 'routes'}`}
                />
              }
            />
            <SshStat
              label="X-Magento-Tags"
              value={
                <StatusBadge
                  kind="auditResult"
                  value={report.missing_tags_count === 0 ? 'passed' : 'warning'}
                  label={report.missing_tags_count === 0 ? 'All present' : `${report.missing_tags_count} missing`}
                />
              }
            />
          </SshStats>

          {report.results.length > 0 ? (
            <ul className="status-list status-list--stacked" aria-label="Routes checked">
              {report.results.map((res, i) => (
                <li key={`${res.path}-${i}`} className="status-list__item">
                  <span className="status-list__name font-mono">{res.path}</span>
                  <StatusBadge kind="cacheability" value={res.is_cacheable ? 'cacheable' : 'private'} />
                  <div className="status-list__detail">
                    HTTP {res.http_status}. Cache-Control: <code>{res.cache_control || 'not set'}</code>.{' '}
                    {res.has_magento_tags ? 'Tags present.' : 'Tags missing.'} {res.sets_cookie ? 'Sets a cookie.' : 'No cookie.'}
                    {res.issue_summary ? <div className="ssh-advice">{res.issue_summary}</div> : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          {report.recommendations.length > 0 ? (
            <Callout tone="warning" title="Recommendations">
              <ul>
                {report.recommendations.map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </Callout>
          ) : null}
        </OperationResult>
      ) : null}

      {!report && !failure ? (
        <p className="quiet-empty">Not checked yet</p>
      ) : null}
    </Section>
  );
};
